import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

// Utils
import settings from "../../../../settings.json";

// Hooks
import { useFetchUser } from "../../../../hooks/useFetchUser";

// Custom components
import Title from "../../../general/Title";
import ContentTable from "../../../general/SimpleTable";
import ContentTableRows from "./ContentTableRows";

// Interfaces
import { File } from "../../../../interfaces/File";

// Firebase
import { firestore } from "../../../../firebase/firebase";
import { collection, doc, QueryDocumentSnapshot, DocumentData, query, orderBy, limit, getDocs, getDoc, endBefore, startAfter, getCountFromServer } from "firebase/firestore";
import { genDoc } from "../../../../utils/firebase";

const Contents: React.FC = () => {
    const { currentAdmin, setLoading, loading } = useFetchUser();
    const hist = useHistory();

    // States
    const [items, setItems] = useState<File[]>([]);
    const [count, setCount] = useState<number>(0);
    const [page, setPage] = useState(0);
    const [firstVisible, setFirstVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);

    const fetchFirstItems = async () => {
        try {
            setLoading(true);
            const localQuery = query(collection(firestore, "Files"), orderBy("createdAt", "desc"), limit(settings.page.rowsPerPage));
            const snapshot = await getDocs(localQuery);
            const data = snapshot.docs.map(genDoc<File>()).filter((item: File) => {
                if (currentAdmin && currentAdmin.roles.includes(settings.app.highestRole)) return true;
                return !item.isDeleted;
            });
            setFirstVisible(snapshot.docs[0] ?? null);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1] ?? null);
            setItems(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleNextPage = async () => {
        if (!lastVisible) return;
        try {
            setLoading(true);
            const localQuery = query(
                collection(firestore, "Files"),
                orderBy("createdAt", "desc"),
                limit(settings.page.rowsPerPage),
                startAfter(lastVisible)
            );
            const snapshot = await getDocs(localQuery);
            if (snapshot.docs.length === 0) return;
            const data = snapshot.docs.map(genDoc<File>()).filter((item: File) => {
                if (currentAdmin && currentAdmin.roles.includes(settings.app.highestRole)) return true;
                return !item.isDeleted;
            });
            setFirstVisible(snapshot.docs[0]);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setItems(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handlePreviousPage = async () => {
        if (!firstVisible) return;
        try {
            setLoading(true);
            const localQuery = query(
                collection(firestore, "Files"),
                orderBy("createdAt", "desc"),
                limit(settings.page.rowsPerPage),
                endBefore(firstVisible)
            );
            const snapshot = await getDocs(localQuery);
            if (snapshot.docs.length === 0) return;
            const data = snapshot.docs.map(genDoc<File>()).filter((item: File) => {
                if (currentAdmin && currentAdmin.roles.includes(settings.app.highestRole)) return true;
                return !item.isDeleted;
            });
            setFirstVisible(snapshot.docs[0]);
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
            setItems(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchCount = async () => {
        try {
            const snapshot = await getCountFromServer(collection(firestore, "Files"));
            setCount(snapshot.data().count);
        } catch (e) {
            console.error(e);
        }
    };

    const handlePageChange = (e: any, newPage: number) => {
        if (loading) return;
        if (page < newPage) handleNextPage();
        else if (page > newPage) handlePreviousPage();
        setPage(newPage);
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchCount(), fetchFirstItems()]).finally(() => setLoading(false));
    }, []); // eslint-disable-line

    return (
        <>
            <Title>Files</Title>

            <ContentTable
                loading={loading}
                data={items}
                count={count}
                rows={ContentTableRows()}
                noMoreData={items.length <= count}
                rowsPerPage={settings.page.rowsPerPage}
                page={page}
                onChangePage={handlePageChange}
                onDoubleClick={(c: File) => hist.push(`/dashboard/content/undefined/undefined/undefined/${c.id}`)}
            />
        </>
    );
};

export default Contents;
